import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { format } from 'date-fns';
import {
  BathIcon,
  BusIcon,
  DoorClosedIcon,
  DoorOpenIcon,
  HammerIcon,
  HouseIcon,
  PlaneIcon,
  RulerIcon,
  SquareParkingIcon,
  TramFrontIcon,
  ZapIcon,
} from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { env } from '@/constants/env';
import type { Estate } from '@/features/estates-property/api/estate';
import { DetailsField } from '@/features/estates-property/components/details-field';
import { OptionsDetailsField } from '@/features/estates-property/components/options-details-field';
import { getRenderValue } from '@/utils/getEmptyVisualValue';

import type { EstateAnalysis } from '../api/analytics';
import HighwayIcon from './icons/highway-icon';

const MAP_BOX_TOKEN = env.MAP_BOX_API_TOKEN;

export const ReportStartPage = ({
  estate,
  estateAnalysis,
}: {
  estate: Estate;
  estateAnalysis: EstateAnalysis;
}) => {
  const { t } = useTranslation();
  const { data: estateOptions } = useEstateOptions();

  const propertyDetails = useMemo(() => {
    return [
      {
        labelKey: 'propertyType',
        icon: <HouseIcon size={16} />,
        value: estate.estateDetails?.propertyType,
        options: estateOptions?.propertyType,
      },
      {
        labelKey: 'buildingType',
        icon: <HouseIcon size={16} />,
        value: estate.estateDetails?.constructionType,
        options: estateOptions?.buildingType,
      },
      {
        labelKey: 'buildingYear',
        icon: <HammerIcon size={16} />,
        value: estate.estateDetails?.buildingYear,
      },
      {
        labelKey: 'livingArea',
        icon: <RulerIcon size={16} />,
        value:
          estate.estateAreasRooms?.livingArea &&
          t('area-value', { value: estate.estateAreasRooms?.livingArea }),
      },
      {
        labelKey: 'numberOfFloors',
        icon: <DoorClosedIcon size={16} />,
        value: estate.estateAreasRooms?.numberOfFloors,
      },
      {
        labelKey: 'totalNumberOfRooms',
        icon: <DoorOpenIcon size={16} />,
        value: estate.estateAreasRooms?.totalNumberOfRooms,
      },
      {
        labelKey: 'numberOfBathrooms',
        icon: <BathIcon size={16} />,
        value: estate.estateAreasRooms?.numberOfBathrooms,
      },
      {
        labelKey: 'numberOfParkingSpaces',
        icon: <SquareParkingIcon size={16} />,
        value: estate.estateAreasRooms?.numberOfParkingSpaces,
      },
      {
        labelKey: 'energyCertificate',
        icon: <ZapIcon size={16} />,
        value: estate.estateEnergyDetails?.hwbClass,
        options: estateOptions?.energyClasses,
      },
    ];
  }, [t, estateOptions, estate]);

  const nearestPlaces = useMemo(() => {
    if (estateAnalysis.pointsOfInterest) {
      const transportPOI = estateAnalysis.pointsOfInterest.data.items.filter(
        (dt) => dt.category === 'transport'
      );
      return [
        {
          labelKey: 'airportName',
          icon: <PlaneIcon size={16} />,
          distance: transportPOI.find((item) => item.subcategory === 'airport')?.distance,
        },
        {
          labelKey: 'highwayAccessName',
          icon: <HighwayIcon />,
          distance: transportPOI.find((item) => item.subcategory === 'highway_ramp')?.distance,
        },
        {
          labelKey: 'busStation',
          icon: <BusIcon size={16} />,
          distance: transportPOI.find((item) => item.subcategory === 'bus')?.distance,
        },
        {
          labelKey: 'trainStation',
          icon: <TramFrontIcon size={16} />,
          distance: transportPOI.find((item) => item.subcategory === 'train')?.distance,
        },
      ];
    }
    return [];
  }, [estateAnalysis]);

  return (
    <div className="print-only">
      <p className="text-[9px] text-text-secondary">
        {t('created')} {format(new Date(), 'dd.MM.yyyy HH:mm')}
      </p>
      <div className="mb-8 grid grid-cols-[1fr_120px] gap-6">
        <div>
          <p className="mb-4 text-[14px] font-bold">{estate.address?.address}</p>
          <div className="grid grid-cols-3 gap-3">
            {propertyDetails.map(({ labelKey, icon, value, options }) => (
              <div key={labelKey} className="flex items-center gap-2">
                {icon}
                {options ? (
                  <OptionsDetailsField
                    label={t(labelKey)}
                    options={options}
                    value={value}
                    className="grid-cols-1 gap-0 [&_p:nth-child(1)]:text-[9px] [&_p:nth-child(1)]:text-foreground [&_p:nth-child(2)]:text-[10px] [&_p:nth-child(2)]:font-bold [&_p]:leading-tight"
                  />
                ) : (
                  <DetailsField
                    label={t(labelKey)}
                    value={value}
                    className="grid-cols-1 gap-0 [&_p:nth-child(1)]:text-[9px] [&_p:nth-child(1)]:text-foreground [&_p:nth-child(2)]:text-[10px] [&_p:nth-child(2)]:font-bold [&_p]:leading-tight"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 space-y-2">
          {nearestPlaces.map(({ labelKey, distance, icon }) => (
            <div key={labelKey} className="flex items-center gap-2">
              <div className="flex min-h-6 min-w-6 items-center justify-center rounded bg-primary-darker text-sm text-white">
                {icon}
              </div>
              <p className="text-[14px] font-normal leading-tight text-black">
                {getRenderValue(distance)}m
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex w-full items-center justify-center">
        <img
          src={`https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/pin-s+07b4c0(${estate?.address?.long},${estate?.address?.lat})/${estate?.address?.long},${estate?.address?.lat},14,0,60/800x700?access_token=${MAP_BOX_TOKEN}`}
          alt="Static map"
        />
      </div>
    </div>
  );
};
